import {
	useAuthentication,
	LoginStep,
} from "@heidelbergmaterials/fcl-authclient"
import type {
	PermissionType,
	UserPermission,
} from "@shared/api/authentication/types"
import { useAuthApiClient } from "@shared/api/client"

import type { AuthState, AuthActions } from "../model/types"
import { useAuthStore } from "../store"

/**
 * Custom hook that wraps FCP's useAuthentication hook and provides
 * both FCP and legacy authentication state/actions for backward compatibility
 *
 * @returns {AuthState & AuthActions} Combined authentication state and actions
 *
 * @example
 * ```tsx
 * const { isAuthenticated, loginState, login, logout } = useAuth()
 *
 * if (!isAuthenticated) {
 *   return <LoginButton onClick={login} />
 * }
 *
 * return <UserProfile loginState={loginState} onLogout={logout} />
 * ```
 */
export const useAuth = (): AuthState & AuthActions => {
	const {
		loginStep,
		decodedAccessToken,
		accessToken,
		startLoginFlow,
		startLogoutFlow,
		redirectToAuthenticator,
		clearStoredTokens,
	} = useAuthentication()

	const {
		userData,
		userPermissions,
		fetchUserProfile: fetchUserProfileOriginal,
		fetchUserPermissions: fetchUserPermissionsOriginal,
		updateUserProfile: updateUserProfileOriginal,
	} = useAuthStore()

	const apiClient = useAuthApiClient()

	const isAuthenticated = loginStep === LoginStep.Authenticated

	const userId = decodedAccessToken?.user_id

	const fetchUserProfile = async () => {
		if (!userId) return
		await fetchUserProfileOriginal(apiClient, userId)
	}

	const fetchUserPermissions = async (): Promise<
		UserPermission[] | undefined
	> => {
		if (!userId) return
		return await fetchUserPermissionsOriginal(apiClient, userId)
	}

	const updateUserProfile = async (patch: Partial<typeof userData>) => {
		if (!userId || !patch) return
		await updateUserProfileOriginal(apiClient, userId, patch)
	}

	const hasAnyPermission = (permissions: PermissionType[]): boolean => {
		if (permissions.length === 0) return false
		return (
			userPermissions?.some(p =>
				permissions.includes(p.permissionType),
			) ?? false
		)
	}

	return {
		loginStep,
		isAuthenticated,
		decodedAccessToken,
		accessToken,
		userData,
		userPermissions,
		startLoginFlow,
		startLogoutFlow,
		redirectToAuthenticator,
		clearStoredTokens,
		fetchUserProfile,
		fetchUserPermissions,
		updateUserProfile,
		hasAnyPermission,
	}
}
